/**
 * Общее ядро рисования боксов для пулов «Боксы» (BoxAnnotator) и «Люди» (PeopleAnnotator).
 *
 * Мышь, как у полигонов в зонировании: клик выбирает бокс; повторный клик в той же точке —
 * следующий из наложенных (цикл идёт от ВЫБРАННОГО, а не от верхнего, иначе третий в стопке
 * недостижим); Ctrl/⌘ + рамка — выделить всё в области; Shift — добавить к выделению;
 * просто рамка на пустом месте — новый бокс. Координаты — доли 0–1 картинки; `view` — окно
 * просмотра в тех же долях (весь кадр или зум на полку).
 */
import { useRef, useState, type ReactNode } from 'react';

/** Меньше этого (в долях картинки) — не бокс, а дрожание руки. */
export const MIN_BOX = 0.01;
/** До этого сдвига в пикселях нажатие считается кликом, не рамкой. */
export const CLICK_PX = 5;

export type Pt = { x: number; y: number };
export type Rect = { x0: number; y0: number; x1: number; y1: number };
export type Dims = { w: number; h: number };
type Drag = {
  mode: 'draw' | 'select' | 'click'; start: Pt; startPx: Pt; cur: Pt; moving: boolean; shift: boolean;
};

/** Минимум, который нужен ядру: геометрия и флаги правки. */
export interface DrawBox extends Rect {
  id: number | null;
  weak: boolean;
  deleted: boolean;
  added: boolean;
}

export type BoxStroke = { stroke: string; strokeWidth: number; fill: string; dash: string | undefined };

export const FULL_VIEW: Rect = { x0: 0, y0: 0, x1: 1, y1: 1 };

export const area = (b: Rect) => Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0);
export const contains = (b: Rect, p: Pt) => p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1;
export const intersects = (a: Rect, b: Rect) => a.x0 <= b.x1 && a.x1 >= b.x0 && a.y0 <= b.y1 && a.y1 >= b.y0;
export const rectOf = (a: Pt, b: Pt): Rect => ({
  x0: Math.min(a.x, b.x), y0: Math.min(a.y, b.y), x1: Math.max(a.x, b.x), y1: Math.max(a.y, b.y),
});
export const sameBoxes = (a: unknown[], b: unknown[]) => JSON.stringify(a) === JSON.stringify(b);
export const fmtTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Базовая палитра: выбранный — синий, удалённый — красный пунктир, дорисованный — фиолетовый,
 *  слабый — серый пунктир, обычный — зелёный. */
export const boxStyle = (b: DrawBox, selected: boolean): BoxStroke => {
  if (selected) return { stroke: '#2563eb', strokeWidth: 3, fill: 'rgba(37,99,235,0.22)', dash: undefined };
  if (b.deleted) return { stroke: '#ef4444', strokeWidth: 1.5, fill: 'rgba(239,68,68,0.12)', dash: '6 4' };
  if (b.added) return { stroke: '#a855f7', strokeWidth: 2, fill: 'rgba(168,85,247,0.12)', dash: undefined };
  if (b.weak) return { stroke: '#9ca3af', strokeWidth: 1, fill: 'rgba(156,163,175,0.08)', dash: '3 3' };
  return { stroke: '#22c55e', strokeWidth: 2, fill: 'rgba(34,197,94,0.08)', dash: undefined };
};

interface CanvasProps<B extends DrawBox> {
  imageUrl: string | null;
  view: Rect;
  /** CSS aspect-ratio окна (картинка растянута по осям через viewBox). */
  aspect: string;
  boxes: B[];
  selected: Set<number>;
  visible: (b: B) => boolean;
  styleOf?: (b: B, selected: boolean) => BoxStroke;
  newBox: (r: Rect) => B;
  onFocus: () => void;
  onSelect: (sel: Set<number>) => void;
  onChange: (boxes: B[]) => void;
  /** Подложка под боксами (например, контуры полок). */
  underlay?: ReactNode;
}

export function BoxCanvas<B extends DrawBox>({
  imageUrl, view, aspect, boxes, selected, visible, styleOf = boxStyle, newBox,
  onFocus, onSelect, onChange, underlay,
}: CanvasProps<B>) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<Drag | null>(null);
  const lastClick = useRef<{ px: Pt; cands: number[] } | null>(null);
  const [band, setBand] = useState<Rect | null>(null);
  const vw = view.x1 - view.x0;
  const vh = view.y1 - view.y0;

  const toPt = (e: React.PointerEvent): Pt => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: view.x0 + ((e.clientX - r.left) / r.width) * vw, y: view.y0 + ((e.clientY - r.top) / r.height) * vh };
  };

  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    onFocus();
    const p = toPt(e);
    const hit = boxes.some(b => visible(b) && contains(b, p));
    drag.current = {
      mode: e.ctrlKey || e.metaKey ? 'select' : hit ? 'click' : 'draw',
      start: p, startPx: { x: e.clientX, y: e.clientY }, cur: p, moving: false, shift: e.shiftKey,
    };
    svgRef.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    d.cur = toPt(e);
    if (!d.moving && Math.hypot(e.clientX - d.startPx.x, e.clientY - d.startPx.y) > CLICK_PX) d.moving = true;
    if (d.moving && d.mode !== 'click') setBand(rectOf(d.start, d.cur));
  };

  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    setBand(null);
    if (!d) return;
    const p = toPt(e);
    if (!d.moving) {
      // Клик. Кандидаты — все боксы под курсором, мелкие первыми: наложенный маленький
      // иначе никогда не выбрать.
      const cands = boxes.map((b, i) => ({ b, i })).filter(({ b }) => visible(b) && contains(b, p))
        .sort((a, c) => area(a.b) - area(c.b)).map(({ i }) => i);
      if (!cands.length) { onSelect(new Set()); lastClick.current = null; return; }
      if (d.shift) {
        const next = new Set(selected);
        if (next.has(cands[0])) next.delete(cands[0]); else next.add(cands[0]);
        onSelect(next);
        return;
      }
      const same = lastClick.current
        && Math.hypot(lastClick.current.px.x - e.clientX, lastClick.current.px.y - e.clientY) <= CLICK_PX * 2;
      const cur = selected.size === 1 ? [...selected][0] : -1;
      const at = cands.indexOf(cur);
      // Второй клик в той же точке — следующий из наложенных, по кругу от выбранного.
      const pick = same && at >= 0 ? cands[(at + 1) % cands.length] : cands[0];
      lastClick.current = { px: { x: e.clientX, y: e.clientY }, cands };
      onSelect(new Set([pick]));
      return;
    }
    const r = rectOf(d.start, d.cur);
    if (d.mode === 'draw') {
      if (r.x1 - r.x0 < MIN_BOX || r.y1 - r.y0 < MIN_BOX) return;
      onChange([...boxes, newBox(r)]);
      onSelect(new Set([boxes.length]));
    } else if (d.mode === 'select') {
      const hits = boxes.map((b, i) => ({ b, i })).filter(({ b }) => visible(b) && intersects(b, r)).map(({ i }) => i);
      onSelect(d.shift ? new Set([...selected, ...hits]) : new Set(hits));
    }
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`${view.x0} ${view.y0} ${vw} ${vh}`}
      preserveAspectRatio="none"
      className="block w-full touch-none select-none rounded bg-black/5"
      style={{ aspectRatio: aspect, cursor: 'crosshair' }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={() => { drag.current = null; setBand(null); }}
    >
      {imageUrl && (
        <image href={imageUrl} x={0} y={0} width={1} height={1} preserveAspectRatio="none" />
      )}
      {underlay}
      {boxes.map((b, i) => {
        if (!visible(b)) return null;
        const st = styleOf(b, selected.has(i));
        return (
          <rect
            key={b.id ?? `n${i}`}
            x={b.x0} y={b.y0} width={Math.max(0, b.x1 - b.x0)} height={Math.max(0, b.y1 - b.y0)}
            fill={st.fill} stroke={st.stroke} strokeWidth={st.strokeWidth} strokeDasharray={st.dash}
            vectorEffect="non-scaling-stroke" pointerEvents="none"
          />
        );
      })}
      {band && (
        <rect
          x={band.x0} y={band.y0} width={band.x1 - band.x0} height={band.y1 - band.y0}
          fill="rgba(37,99,235,0.12)" stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke" pointerEvents="none"
        />
      )}
    </svg>
  );
}
